import React, { useState } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import { compressImage } from '../../utils/imageCompression';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Liste des principales villes de la RDC
const DRC_CITIES = [
  'KINSHASA', 'LUBUMBASHI', 'MBUJI-MAYI', 'KANANGA', 'KISANGANI',
  'BUKAVU', 'GOMA', 'MATADI', 'BOMA', 'KOLWEZI', 'LIKASI', 'TSHIKAPA',
  'KIKWIT', 'MBANDAKA', 'UVIRA', 'BUTEMBO', 'BENI', 'KASONGO',
  'BANDUNDU', 'KINDU', 'KALEMIE', 'GEMENA', 'ISIRO', 'BUNIA',
  'MUANDA', 'MOANDA', 'INONGO', 'ZONGO', 'AUTRE'
];

function DoctorFormModal({ doctor, onSave, onClose }) {
  const [formData, setFormData] = useState(doctor || {
    name: '',
    specialty: '',
    bio: '',
    photo_url: '',
    city: '',
    otherCity: '',
    email: '',
    password: '',
    phone: '',
    available_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
  });
  const [uploading, setUploading] = useState(false);
  const [showOtherCity, setShowOtherCity] = useState(doctor?.city && !DRC_CITIES.includes(doctor.city));
  const [showPassword, setShowPassword] = useState(false);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = {
    monday: 'Lun',
    tuesday: 'Mar',
    wednesday: 'Mer',
    thursday: 'Jeu',
    friday: 'Ven',
    saturday: 'Sam',
    sunday: 'Dimanche'
  };

  const handleCityChange = (e) => {
    const value = e.target.value;
    if (value === 'AUTRE') {
      setShowOtherCity(true);
      setFormData({ ...formData, city: value, otherCity: '' });
    } else {
      setShowOtherCity(false);
      setFormData({ ...formData, city: value, otherCity: '' });
    }
  };

  const toggleDay = (day) => {
    const newDays = formData.available_days.includes(day)
      ? formData.available_days.filter(d => d !== day)
      : [...formData.available_days, day];
    setFormData({ ...formData, available_days: newDays });
  };

  const handlePhotoUpload = async (e) => {
    let file = e.target.files[0];
    if (!file) return;
    file = await compressImage(file);

    if (doctor && doctor.doctor_id) {
      setUploading(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      try {
        const response = await fetch(`${API_URL}/api/admin/doctors/${doctor.doctor_id}/photo`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` },
          body: uploadFormData
        });

        if (response.ok) {
          const data = await response.json();
          setFormData(prev => ({ ...prev, photo_url: data.photo_url }));
          alert('Photo mise à jour !');
        } else {
          const err = await response.json();
          alert(err.detail || 'Erreur lors de l\'upload');
        }
      } catch (error) {
        console.error('Upload error:', error);
        alert('Erreur de connexion');
      } finally {
        setUploading(false);
      }
    } else {
      setFormData(prev => ({ ...prev, photo_file: file, photo_url: URL.createObjectURL(file) }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalCity = (formData.city === 'AUTRE' || (formData.otherCity && !DRC_CITIES.includes(formData.city)))
      ? (formData.otherCity || formData.city).trim().toUpperCase()
      : formData.city;

    const dataToSave = {
      ...formData,
      city: finalCity
    };
    delete dataToSave.otherCity;
    onSave(dataToSave);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{doctor ? 'Modifier le Médecin' : 'Nouveau Médecin'}</h2>
          <button onClick={onClose} className="modal-close">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label>Nom complet *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Dr. Jean Dupont"
              />
            </div>
            <div className="form-group">
              <label>Spécialité *</label>
              <input
                type="text"
                value={formData.specialty}
                onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                required
                placeholder="Médecine Générale"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Ville *</label>
              <select
                value={DRC_CITIES.includes(formData.city) ? formData.city : (formData.city ? 'AUTRE' : '')}
                onChange={handleCityChange}
                required
              >
                <option value="">-- Sélectionnez une ville --</option>
                {DRC_CITIES.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
              {(showOtherCity || (formData.city && !DRC_CITIES.includes(formData.city))) && (
                <input
                  type="text"
                  placeholder="Précisez la ville"
                  value={formData.otherCity || (formData.city !== 'AUTRE' ? formData.city : '')}
                  onChange={(e) => setFormData({ ...formData, otherCity: e.target.value })}
                  style={{ marginTop: '0.5rem' }}
                  required
                />
              )}
            </div>
            <div className="form-group">
              <label>Photo du médecin</label>
              <div className="photo-upload-container">
                {formData.photo_url && (
                  <img
                    src={formData.photo_url.startsWith('/api') ? `${API_URL}${formData.photo_url}` : formData.photo_url}
                    alt="Preview"
                    className="photo-preview"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoUpload}
                  disabled={uploading}
                  className="photo-input"
                />
                <p className="photo-hint">{uploading ? 'Upload en cours...' : 'JPG, PNG ou WebP. Max 5MB'}</p>
              </div>
            </div>
          </div>
          <div className="form-group">
            <label>Biographie</label>
            <textarea
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              rows="2"
              placeholder="Courte description du médecin..."
            />
          </div>
          <div className="form-divider">
            <span>Accès Téléconsultation {!doctor && '*'}</span>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Email de connexion {!doctor && '*'}</label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="medecin@clinique.cd"
                required={!doctor}
              />
            </div>
            <div className="form-group">
              <label>Mot de passe {!doctor && '*'}</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password || ''}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={doctor ? '(laisser vide pour ne pas changer)' : '••••••••'}
                  required={!doctor}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Téléphone WhatsApp (pour notifications)</label>
              <input
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+243858445231"
              />
              <small style={{ color: '#666', fontSize: '0.75rem' }}>Format: +243XXXXXXXXX</small>
            </div>
          </div>
          <div className="form-group">
            <label>Jours disponibles</label>
            <div className="days-selector">
              {days.map(day => (
                <button
                  key={day}
                  type="button"
                  className={`day-btn ${formData.available_days.includes(day) ? 'selected' : ''}`}
                  onClick={() => toggleDay(day)}
                >
                  {dayLabels[day]}
                </button>
              ))}
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Annuler
            </button>
            <button type="submit" className="btn-primary">
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DoctorFormModal;
